import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, User, MessageSquare, Wallet, Send, CheckCircle2, ShieldCheck, HelpCircle, UserCheck } from "lucide-react";
import { useT } from "../i18n/LanguageContext";
import { track } from "../lib/analytics";
import { MailDeliveryHint } from "./MailDeliveryHint";
import { BACKEND_URL } from "../lib/apiBase";

const API = `${BACKEND_URL}/api`;

// Match: starts with +, then 1-4 country digits, then ≥6 chars of digits/spaces/dashes/parens
const PHONE_REGEX = /^\+\d{1,4}[\s\d\-()]{6,30}$/;

const initialForm = {
  name: "",
  email: "",
  phone: "",
  investment_size: "",
  message: "",
  heard_from: "",
  referrer_name: "",
  partner_id: null,
  agb_accepted: false,
  consent_contact: false,
};

export const Contact = () => {
  const { t, lang } = useT();
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [started, setStarted] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);

  const phoneValid = PHONE_REGEX.test(form.phone);

  const update = (k) => (e) => {
    if (!started) {
      setStarted(true);
      track("form_start");
    }
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => {
      const next = { ...f, [k]: value };
      // If user switches heard_from away from "referral", clear referrer fields
      if (k === "heard_from" && value !== "referral") {
        next.referrer_name = "";
        next.partner_id = null;
      }
      return next;
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message || !form.phone) {
      toast.error(t.contact.missingFields);
      return;
    }
    if (!phoneValid) {
      toast.error(t.contact.phoneInvalid);
      setPhoneTouched(true);
      return;
    }
    if (!form.agb_accepted || !form.consent_contact) {
      toast.error(t.contact.mustAccept);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        heard_from: form.heard_from || null,
        referrer_name: form.heard_from === "referral" ? (form.referrer_name || null) : null,
        partner_id: form.heard_from === "referral" ? form.partner_id : null,
        // Send transactional + drip mails in the visitor's current site language.
        mail_language: lang === "en" ? "en" : "de",
      });
      toast.success(t.contact.successToast);
      track("form_submit", { meta: { investment_size: form.investment_size || null, heard_from: form.heard_from || null } });
      setDone(true);
      setForm(initialForm);
      setPhoneTouched(false);
    } catch {
      toast.error(t.contact.errorToast);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="kontakt" data-testid="contact-section" className="relative py-24 sm:py-32">
      <div className="al-blob" style={{ bottom: "-120px", left: "-100px", width: 380, height: 380, background: "radial-gradient(closest-side, rgba(74,143,231,0.4), transparent)" }} />
      <div className="al-blob" style={{ top: "-80px", right: "-100px", width: 380, height: 380, background: "radial-gradient(closest-side, rgba(168,85,247,0.4), transparent)" }} />

      <div className="relative max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <span className="al-tag">{t.contact.tag}</span>
          <h2 className="mt-5 font-display font-bold text-4xl sm:text-5xl leading-tight">
            {t.contact.h2a}<span className="al-grad-text">{t.contact.h2b}</span>
          </h2>
          <p className="mt-5 text-slate-600 leading-relaxed">{t.contact.sub}</p>

          <div className="mt-8 space-y-3">
            <div data-testid="contact-info-response" className="al-card p-4 flex items-center gap-3">
              <span className="h-10 w-10 rounded-xl al-grad-bg flex items-center justify-center text-white">
                <CheckCircle2 className="h-4 w-4" />
              </span>
              <div>
                <div className="text-xs uppercase tracking-widest font-semibold text-slate-500">{t.contact.respTitle}</div>
                <div className="font-display font-semibold text-slate-900">{t.contact.respVal}</div>
              </div>
            </div>
            <div data-testid="contact-info-privacy" className="al-card p-4 flex items-center gap-3">
              <span className="h-10 w-10 rounded-xl al-grad-soft-bg flex items-center justify-center text-[var(--al-purple)]">
                <ShieldCheck className="h-4 w-4" />
              </span>
              <div>
                <div className="text-xs uppercase tracking-widest font-semibold text-slate-500">{t.contact.privTitle}</div>
                <div className="font-display font-semibold text-slate-900">{t.contact.privVal}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form onSubmit={onSubmit} data-testid="contact-form" className="al-card p-7 sm:p-9 space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field icon={<User className="h-4 w-4" />} label={`${t.contact.name} ${t.contact.required}`}>
                <input data-testid="contact-input-name" required type="text" value={form.name} onChange={update("name")} className="al-input" placeholder={t.contact.placeholderName} />
              </Field>
              <Field icon={<Mail className="h-4 w-4" />} label={`${t.contact.email} ${t.contact.required}`}>
                <input data-testid="contact-input-email" required type="email" value={form.email} onChange={update("email")} className="al-input" placeholder={t.contact.placeholderEmail} />
              </Field>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <Field icon={<Phone className="h-4 w-4" />} label={`${t.contact.phone} ${t.contact.required}`}>
                <input
                  data-testid="contact-input-phone"
                  required
                  type="tel"
                  value={form.phone}
                  onChange={update("phone")}
                  onBlur={() => setPhoneTouched(true)}
                  className={`al-input ${phoneTouched && form.phone && !phoneValid ? "!border-rose-400 focus:!border-rose-500" : ""}`}
                  placeholder={t.contact.placeholderPhone}
                />
                {phoneTouched && form.phone && !phoneValid && (
                  <span className="block mt-1 text-[11px] text-rose-600">{t.contact.phoneInvalid}</span>
                )}
              </Field>
              <Field icon={<Wallet className="h-4 w-4" />} label={t.contact.invest}>
                <select data-testid="contact-input-investment" value={form.investment_size} onChange={update("investment_size")} className="al-input">
                  <option value="">{t.contact.placeholderInvest}</option>
                  {t.contact.sizes.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
            </div>

            {/* How did you find us */}
            <div className="grid sm:grid-cols-2 gap-5">
              <Field icon={<HelpCircle className="h-4 w-4" />} label={t.contact.heardFrom}>
                <select data-testid="contact-input-heard-from" value={form.heard_from} onChange={update("heard_from")} className="al-input">
                  <option value="">{t.contact.heardFromPlaceholder}</option>
                  {t.contact.heardFromOptions.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </Field>

              {form.heard_from === "referral" && (
                <Field icon={<UserCheck className="h-4 w-4" />} label={t.contact.referrer}>
                  <PartnerAutocomplete
                    value={form.referrer_name}
                    onChange={(name, partnerId) =>
                      setForm((f) => ({ ...f, referrer_name: name, partner_id: partnerId }))
                    }
                    placeholder={t.contact.referrerPlaceholder}
                  />
                </Field>
              )}
            </div>

            <Field icon={<MessageSquare className="h-4 w-4" />} label={`${t.contact.message} ${t.contact.required}`}>
              <textarea data-testid="contact-input-message" required rows={5} value={form.message} onChange={update("message")} className="al-input resize-none" placeholder={t.contact.placeholderMessage} />
            </Field>

            {/* Required checkboxes */}
            <div className="space-y-3 pt-1">
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  data-testid="contact-checkbox-agb"
                  type="checkbox"
                  required
                  checked={form.agb_accepted}
                  onChange={update("agb_accepted")}
                  className="mt-0.5 h-4 w-4 accent-[var(--al-purple)] cursor-pointer flex-shrink-0"
                />
                <span className="text-sm text-slate-600 leading-relaxed">
                  {t.contact.agbAcceptPre}
                  <Link to="/agb" target="_blank" className="text-[var(--al-purple)] underline hover:no-underline">{t.contact.agbAcceptLink}</Link>
                  {t.contact.agbAcceptPost} <span className="text-rose-500">*</span>
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  data-testid="contact-checkbox-consent"
                  type="checkbox"
                  required
                  checked={form.consent_contact}
                  onChange={update("consent_contact")}
                  className="mt-0.5 h-4 w-4 accent-[var(--al-purple)] cursor-pointer flex-shrink-0"
                />
                <span className="text-sm text-slate-600 leading-relaxed">
                  {t.contact.consentContact} <span className="text-rose-500">*</span>
                </span>
              </label>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
              <p className="text-xs text-slate-500 max-w-md">{t.contact.consent}</p>
              <button type="submit" disabled={loading} data-testid="contact-submit-button" className="al-cta disabled:opacity-60 disabled:cursor-not-allowed">
                {loading ? t.contact.sending : done ? t.contact.resend : t.contact.submit}
                <Send className="h-4 w-4" />
              </button>
            </div>
            {done && <MailDeliveryHint testid="contact-mail-hint" className="mt-2" />}
          </form>
        </div>
      </div>

      <style>{`
        .al-input { width: 100%; padding: 12px 14px; border-radius: 12px; border: 1px solid var(--al-line); background: white; font-family: 'Manrope', sans-serif; font-size: 15px; color: var(--al-ink); transition: border-color 200ms ease, box-shadow 200ms ease; }
        .al-input:focus { outline: none; border-color: var(--al-purple); box-shadow: 0 0 0 4px rgba(139,92,246,0.12); }
      `}</style>
    </section>
  );
};

const Field = ({ icon, label, children }) => (
  <label className="block">
    <span className="flex items-center gap-1.5 text-xs uppercase tracking-widest font-semibold text-slate-500 mb-1.5">
      {icon} {label}
    </span>
    {children}
  </label>
);

// Partner autocomplete combobox: shows suggestions only after ≥2 chars,
// preserves free text if no match selected.
const PartnerAutocomplete = ({ value, onChange, placeholder }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const debounceRef = useRef(null);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value || value.length < 2) {
      setItems([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setBusy(true);
      try {
        const res = await axios.get(`${API}/partners/search`, { params: { q: value } });
        setItems(res.data || []);
        setOpen(true);
      } catch {
        setItems([]);
      } finally {
        setBusy(false);
      }
    }, 200);
    return () => clearTimeout(debounceRef.current);
  }, [value]);

  // Click-outside to close suggestions
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const select = (p) => {
    onChange(p.name, p.id);
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <input
        data-testid="contact-input-referrer"
        type="text"
        value={value}
        onChange={(e) => {
          onChange(e.target.value, null); // typing again clears partner_id until a match is selected
          if (e.target.value.length >= 2) setOpen(true);
        }}
        onFocus={() => value.length >= 2 && items.length > 0 && setOpen(true)}
        placeholder={placeholder}
        className="al-input"
        autoComplete="off"
      />
      {open && items.length > 0 && (
        <ul
          data-testid="partner-suggestions"
          className="absolute z-20 left-0 right-0 mt-1 bg-white border border-[var(--al-line)] rounded-xl shadow-lg max-h-60 overflow-y-auto"
        >
          {items.map((p) => (
            <li
              key={p.id}
              data-testid={`partner-suggestion-${p.id}`}
              onMouseDown={() => select(p)}
              className="px-4 py-2.5 text-sm cursor-pointer hover:bg-[var(--al-grad-soft)] al-grad-soft-bg-hover transition-colors flex items-center gap-2"
            >
              <UserCheck className="h-3.5 w-3.5 text-[var(--al-purple)]" />
              <span className="font-display font-semibold">{p.name}</span>
            </li>
          ))}
        </ul>
      )}
      {busy && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-slate-400">Suche…</span>
      )}
    </div>
  );
};
